import { useNavigate } from "react-router";
import { Heading, Flex, Division, Paragraph, EmptyState, Icons, Button } from "@ir/ui";
import { Fragment } from "react/jsx-runtime";
import { HasAllPermissions } from "@/presentation/workspace";
import useDeductionIncomplete from "./hook/useDeductionIncomplete";
import { WorkspacePermissions } from "@/core/permissions";
import { TDeductionResolutionStatus } from "./interface/deductionIncomplete.interface";
import BackupDocuments from "./BackupDocuments";
import { useUserPermissions } from "@/hooks";
import { getMaxFilesAllowed } from "./context/DeductionIncompleteContext";
import { WORKSPACE_ROUTES } from "@/routes/workspace/utils/Workspace.router.utils";

const getStatusClassNames = (deductionResolutionStatus: TDeductionResolutionStatus) => {
  if (deductionResolutionStatus === "failed") return "icon-bg-error";
  return "icon-bg-success";
};

const getStatusIcon = (deductionResolutionStatus: TDeductionResolutionStatus) => {
  if (deductionResolutionStatus === "failed") return Icons.AlertCircle;
  return Icons.Hourglass01;
};

const getTitle = (deductionResolutionStatus: TDeductionResolutionStatus) => {
  return (
    <Heading level={6} className="font-sm font-bold mt-3">
      {deductionResolutionStatus === "failed" ? "We Couldn’t Process Your Document" : "We’re Processing Your Document"}
    </Heading>
  );
};

const GetDescription = (deductionResolutionStatus: TDeductionResolutionStatus) => {
  const { hasAnyPermission } = useUserPermissions();
  const { documents, importedFiles, importedFilesAsDocuments, purchaserKey } = useDeductionIncomplete();
  const navigate = useNavigate();

  const getFailedMessage = () => {
    const hasPermissions = hasAnyPermission([
      WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD,
      WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL,
    ]);
    return hasPermissions ? "Please choose one of the following options:" : "";
  };

  // Combine server documents with imported files
  const getAllDocuments = () => {
    const serverDocuments = documents || [];
    return [...serverDocuments, ...importedFilesAsDocuments];
  };

  // Check if upload button should be disabled
  const isUploadButtonDisabled = () => {
    const maxFilesAllowed = getMaxFilesAllowed(purchaserKey);
    const hasProcessingFiles = importedFiles.some(file => file.isPending);
    const isAtFileLimit = getAllDocuments().length >= maxFilesAllowed;

    return isAtFileLimit || hasProcessingFiles;
  };

  // Get tooltip message for disabled upload button
  const getUploadButtonTooltip = () => {
    if (!isUploadButtonDisabled()) return undefined;

    const maxFilesAllowed = getMaxFilesAllowed(purchaserKey);
    return getAllDocuments().length >= maxFilesAllowed ? `Maximum ${maxFilesAllowed} files allowed` : "Files are currently being processed";
  };

  return (
    <>
      <Flex direction="column" justifyContent="center" align="center">
        <Division className="font-regular text-xs font-semibold text-center">
          {deductionResolutionStatus === "failed" ? (
            <Paragraph className="mb-1">Something went wrong while trying to extract the data. {getFailedMessage()}</Paragraph>
          ) : (
            <Fragment>
              <Paragraph className="mb-1">Our Intelli AI is analyzing the file and extracting deduction details. This may take a moment.</Paragraph>
              <Paragraph>You can safely navigate away, and check back later.</Paragraph>
            </Fragment>
          )}
        </Division>
      </Flex>
      {deductionResolutionStatus === "failed" && (
        <Flex direction="row" justifyContent="center" className="mt-3">
          <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD]}>
            <Button
              color="primary"
              onPress={() => navigate(`/workspace${WORKSPACE_ROUTES.INTELLI_UPLOAD}`)}
              isDisabled={isUploadButtonDisabled()}
              htmlTitle={getUploadButtonTooltip()}
            >
              Upload Another
            </Button>
          </HasAllPermissions>

          <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL]}>
            <Button color="primary">Enter Manually</Button>
          </HasAllPermissions>
        </Flex>
      )}
    </>
  );
};

const DeductionIncompleteProcessing = () => {
  const { deductionResolutionStatus, isLoadingDeductionStatus, importedFilesAsDocuments } = useDeductionIncomplete();

  return (
    <>
      <EmptyState
        className={`bg-white px-4 py-5 ${getStatusClassNames(deductionResolutionStatus)}`}
        icon={getStatusIcon(deductionResolutionStatus)}
        title={getTitle(deductionResolutionStatus)}
        description={GetDescription(deductionResolutionStatus)}
      />
      <br />
      {!isLoadingDeductionStatus && deductionResolutionStatus !== "incomplete" && importedFilesAsDocuments.length > 0 && <BackupDocuments />}
    </>
  );
};

export default DeductionIncompleteProcessing;
